import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, Button, Upload, Layout, Typography, notification, Select, Spin } from "antd";
import { UploadOutlined, SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import Logo from "../../assets/images/main-logo-svg.svg";
import "./styles.scss";
import { getAllProjectsDropDown, getProductById, updateProduct } from "../../api";

const { Option } = Select;
const { Header, Content } = Layout;
const { Title } = Typography;

const EditProduct = () => {
    const { productId } = useParams(); 
    const [loading, setLoading] = useState(false); // ✅ Only for button
    const [formLoading, setFormLoading] = useState(true); // ✅ For initial form loading
    const [form] = Form.useForm();
    const history = useNavigate();
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        const fetchProductDetails = async () => {
            try {
                const response = await getProductById(productId);
                if (response.data) {
                    const product = response.data.product;

                    form.setFieldsValue({
                        name: product.name,
                        price: product.price,
                        description: product.description,
                        projectId: product.project?._id || null, // Select project ID
                    });
                }
            } catch (error) {
                console.error("Error fetching product:", error);
                notification.error({ message: "Failed to load product details!" });
            }
            setFormLoading(false); // ✅ Stop full-page loader
        };

        fetchProductDetails();
    }, [productId, form]);

    useEffect(() => {
        const fetchProjects = async () => {
            setProjectsLoading(true);
            try {
                const response = await getAllProjectsDropDown();
                if (response.data && response.data.projects) {
                    setProjects(response.data.projects.map((item) => ({
                        label: item.projectName,
                        value: item._id,
                    })));
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
                notification.error({ message: "Failed to load projects!" });
            }
            setProjectsLoading(false);
        };

        fetchProjects();
    }, []);

    //  Handle Form Submission
    const handleSubmit = async (values) => {
        setLoading(true); // ✅ Only disable button
        try {
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("price", values.price);
            formData.append("description", values.description);
            formData.append("project", values.projectId); //  Submit selected project ID

            if (values.background?.file) {
                formData.append("background", values.background.file);
            }

            await updateProduct(productId, formData); // Submit to API

            notification.success({ message: "Product updated successfully!" });
            history("/my-products"); // Redirect to product list
        } catch (error) {
            console.error("Error updating product:", error);
            notification.error({ message: error?.response?.data?.message || "Failed to update product!" });
        } finally {
            setLoading(false);
        }
    };

    const goToHome = useCallback(() => history("/my-products"), [history]);

    return (
        <Layout className="add-product">
            <Header className="add-product__header">
                <div className="header-layout">
                    <div className="left">
                        <div className="header-brand" onClick={() => history("/")} style={{ cursor: "pointer" }}>
                            <img src={Logo} alt="Logo" />
                        </div>
                        <Button onClick={() => goToHome()}>
                            <ArrowLeftOutlined /> Back
                        </Button>
                    </div>
                </div>
            </Header>

            <Content className="add-product__content">
                <div className="form-container">
                    <Title level={3}>Edit Product</Title>
                    {formLoading ? (
                        <Spin size="large" />
                    ) : (
                        <Form form={form} layout="vertical" onFinish={handleSubmit}>
                            {/* Product Name */}
                            <Form.Item
                                label="Product Name"
                                name="name"
                                rules={[{ required: true, message: "Please enter product name" }]}
                            >
                                <Input placeholder="Enter product name" disabled={loading} />
                            </Form.Item>

                            {/*  Select Project (Mandatory) */}
                            <Form.Item
                                label="Select Project"
                                name="projectId"
                                rules={[{ required: true, message: "Please select a project" }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Search & Select Project"
                                    optionFilterProp="label"
                                    loading={projectsLoading}
                                    disabled={loading}
                                    filterOption={(input, option) =>
                                        option?.label?.toLowerCase()?.includes(input?.toLowerCase())
                                    }
                                >
                                    {projects.map((project) => (
                                        <Option key={project.value} value={project.value} label={project.label}>
                                            {project.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/*  Product Price */}
                            <Form.Item
                                label="Price"
                                name="price"
                                rules={[{ required: true, message: "Please enter price" }]}
                            >
                                <Input type="number" placeholder="Enter product price" disabled={loading} />
                            </Form.Item>

                            {/* Product Description */}
                            <Form.Item label="Description" name="description">
                                <Input.TextArea rows={4} placeholder="Enter product description" disabled={loading} />
                            </Form.Item>

                            {/*  Background Image Upload */}
                            <Form.Item label="Upload Background Image" name="background">
                                <Upload beforeUpload={() => false} maxCount={1} disabled={loading}>
                                    <Button icon={<UploadOutlined />} disabled={loading}>
                                        Upload Background
                                    </Button>
                                </Upload>
                            </Form.Item>

                            {/* Submit Button */}
                            <Form.Item>
                                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} disabled={loading}>
                                    Update Product
                                </Button>
                            </Form.Item>
                        </Form>
                    )}
                </div>
            </Content>
        </Layout>
    );
};

export default EditProduct;
